import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type DisplayCurrency = "COP" | "USD";
type SourceCurrency = "COP" | "USD" | "EUR" | "MXN";

type ExchangeRates = Record<SourceCurrency, number>;

interface CurrencyContextValue {
  currency: DisplayCurrency;
  setCurrency: (currency: DisplayCurrency) => void;
  formatPrice: (amount: number, sourceCurrency?: SourceCurrency) => string;
  toCop: (amount: number, sourceCurrency?: SourceCurrency) => number;
  isLiveRate: boolean;
}

const DEFAULT_CURRENCY: DisplayCurrency = "COP";
const CURRENCY_STORAGE_KEY = "socrattica.display-currency";
const RATE_CACHE_KEY = "socrattica.exchange-rates.cop";
const RATE_CACHE_DURATION_MS = 12 * 60 * 60 * 1_000;
const EXCHANGE_RATE_URL = "https://open.er-api.com/v6/latest/COP";

const FALLBACK_RATES: ExchangeRates = {
  COP: 1,
  USD: 0.000312,
  EUR: 0.000274,
  MXN: 0.005451,
};

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

function isDisplayCurrency(value: unknown): value is DisplayCurrency {
  return value === "COP" || value === "USD";
}

function readRates(value: unknown): ExchangeRates | null {
  if (!value || typeof value !== "object") return null;
  const rates = value as Record<string, unknown>;
  const entries = ["COP", "USD", "EUR", "MXN"] as const;

  if (entries.some((currency) => typeof rates[currency] !== "number")) return null;

  return {
    COP: rates.COP as number,
    USD: rates.USD as number,
    EUR: rates.EUR as number,
    MXN: rates.MXN as number,
  };
}

function formatCurrency(amount: number, currency: DisplayCurrency) {
  try {
    const formattedAmount = new Intl.NumberFormat("es-CO", {
      minimumFractionDigits: currency === "COP" ? 0 : 2,
      maximumFractionDigits: currency === "COP" ? 0 : 2,
    }).format(amount);
    return `$${formattedAmount} ${currency}`;
  } catch {
    return `$${amount.toFixed(currency === "COP" ? 0 : 2)} ${currency}`;
  }
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<DisplayCurrency>(DEFAULT_CURRENCY);
  const [rates, setRates] = useState<ExchangeRates>(FALLBACK_RATES);
  const [isLiveRate, setIsLiveRate] = useState(false);

  useEffect(() => {
    try {
      const savedCurrency = localStorage.getItem(CURRENCY_STORAGE_KEY);
      if (isDisplayCurrency(savedCurrency)) setCurrencyState(savedCurrency);

      const cached = JSON.parse(localStorage.getItem(RATE_CACHE_KEY) ?? "null") as {
        rates?: unknown;
        savedAt?: unknown;
      } | null;
      const cachedRates = readRates(cached?.rates);
      const savedAt = typeof cached?.savedAt === "number" ? cached.savedAt : 0;

      if (cachedRates) {
        setRates(cachedRates);
        setIsLiveRate(true);
      }

      if (cachedRates && Date.now() - savedAt < RATE_CACHE_DURATION_MS) return;
    } catch {
      // Si el navegador bloquea el almacenamiento, la moneda sigue funcionando en memoria.
    }

    const controller = new AbortController();

    void fetch(EXCHANGE_RATE_URL, { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Exchange rate ${response.status}`);
        const data = (await response.json()) as { result?: unknown; rates?: unknown };
        if (data.result !== "success") throw new Error("Exchange rate unavailable");
        const nextRates = readRates(data.rates);
        if (!nextRates) throw new Error("Invalid exchange rates");

        setRates(nextRates);
        setIsLiveRate(true);
        try {
          localStorage.setItem(
            RATE_CACHE_KEY,
            JSON.stringify({ rates: nextRates, savedAt: Date.now() }),
          );
        } catch {
          // La tasa en memoria es suficiente para esta visita.
        }
      })
      .catch((error) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setIsLiveRate(false);
      });

    return () => controller.abort();
  }, []);

  const setCurrency = useCallback((nextCurrency: DisplayCurrency) => {
    setCurrencyState(nextCurrency);
    try {
      localStorage.setItem(CURRENCY_STORAGE_KEY, nextCurrency);
    } catch {
      // La preferencia permanece activa durante esta visita.
    }
  }, []);

  const formatPrice = useCallback(
    (amount: number, sourceCurrency: SourceCurrency = "COP") => {
      const sourceRate = rates[sourceCurrency] || 1;
      const amountInCop = sourceCurrency === "COP" ? amount : amount / sourceRate;
      const converted = currency === "COP" ? amountInCop : amountInCop * rates.USD;
      return formatCurrency(converted, currency);
    },
    [currency, rates],
  );

  const toCop = useCallback(
    (amount: number, sourceCurrency: SourceCurrency = "COP") =>
      sourceCurrency === "COP" ? amount : amount / (rates[sourceCurrency] || 1),
    [rates],
  );

  const value = useMemo<CurrencyContextValue>(
    () => ({ currency, setCurrency, formatPrice, toCop, isLiveRate }),
    [currency, formatPrice, isLiveRate, setCurrency, toCop],
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) throw new Error("useCurrency debe usarse dentro de CurrencyProvider.");
  return context;
}
